import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { patchProductsAfterPriceSearch } from '@/api/public';
import BasicButton from '@/components/Common/Button/BasicButton';
import FloatingIconButton from '@/components/Common/Button/FloatingIconButton';
import AlertModal from '@/components/Common/Modal/AlertModal';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import MenuHeader from '@/components/Header/MenuHeader';
import { InformationCircleIcon, PhoneIcon } from '@/components/Icon';
import DirectGuide from '@/components/Products/Sale/DirectGuide';
import SpeedGuide from '@/components/Products/Sale/SpeedGuide';
import { colors } from '@/const/colors';
import { REPRESENTATIVE_NUMBER } from '@/const/common';
import { PRODUCTS_CAN_SALES_MAX_THREE } from '@/const/errorCode';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { PRODUCT_TYPE_DIRECT, PRODUCT_TYPE_SPEED, SALESTYPE } from '@/const/products';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { useMemberContext } from '@/contexts/Member/MemberContext';
import useNiceMobileAuth from '@/hooks/useNiceMobileAuth';
import DefaultHelmet from '@/metadatas/DefaultHelmet';
import { memberAtom } from '@/store/member';
import { isLengthOnlyLoadedType } from '@/utils/Products/productUtils';
import { phoneCall } from '@/utils/common';

type SalesTypeKey = keyof typeof SALESTYPE;

const MyCarSaleGuide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type: paramType } = useParams<{ type?: string }>();
  const [searchParams] = useSearchParams();
  const queryType = searchParams.get('type');

  const [type, setType] = useState('');

  const { showToast } = useToastContext();
  const { getMember } = useMemberContext();
  const [isShowErrorPopup, setIsShowErrorPopup] = useState(false);
  const [isMismatchOpen, setIsMismatchOpen] = useState(false);

  const [salesType, setSalesType] = useState<SalesTypeKey>();

  const [memberAtomData] = useAtom(memberAtom);
  // 회원 데이터가 로드된 뒤에만 판단 (id 유무로 로드 여부 확인)
  const isMemberLoaded = !!memberAtomData?.id;
  const isVerified = Number(memberAtomData?.authentication) === 1;
  // 실명인증이 안 된 일반 회원이 직거래 판매로 진입하면 본인인증 안내를 노출한다.
  const showVerifyGuide = salesType === PRODUCT_TYPE_DIRECT && isMemberLoaded && !isVerified;

  const { isLoading: isVerifying, startVerification } = useNiceMobileAuth({
    memberId: memberAtomData?.id,
    onSuccess: () => {
      if (memberAtomData?.id) {
        getMember?.(memberAtomData.id);
      }
      showToast('본인인증이 완료되었습니다.', 'success', 'bottom');
    },
    onError: (message) => showToast(message, 'error', 'bottom'),
    onPhoneMismatch: () => setIsMismatchOpen(true),
  });

  const handleClickVerify = () => {
    if (isVerifying) return;
    startVerification();
  };

  const handleClickSalesType = () => {
    if (!!location.state?.productId) {
      patchAfterPriceSearchMutation.mutate(String(location.state?.productId));
    } else {
      navigate('/products/sales', { state: { type: salesType }, replace: true });
    }
  };

  const onClickGoMyProductPage = () => {
    navigate('/products/my');
  };

  const patchAfterPriceSearchMutation = useMutation((request: string) => patchProductsAfterPriceSearch(request), {
    onSuccess: (response) => {
      if (isLengthOnlyLoadedType(response) || response?.loaded === '' || response?.loaded === null) {
        navigate(`/products/sales/price-trend/${response.id}`, { state: { type: salesType } });
      } else {
        navigate(`/products/sales/loaded/${response.id}`, { state: { type: salesType } });
      }
    },
    onError: (error: AxiosError) => {
      if (error.code === PRODUCTS_CAN_SALES_MAX_THREE) {
        showToast(error.message, 'error', 'bottom');
      } else if (error.code === 'ALREADY_EXIST_SALE_OR_PAUSE_PRODUCT') {
        setIsShowErrorPopup(true);
      } else {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      }
    },
  });

  useEffect(() => {
    const resolvedType = location.state?.type || paramType || queryType;

    setSalesType(resolvedType as SalesTypeKey);
    setType(resolvedType);
  }, [location.state, paramType, queryType]);

  const buttonLabel = type === 'DIRECT' ? '내 차 직접 팔기' : type === 'SPEED' ? '직트럭에 견적 요청' : '판매 등록';

  return (
    <>
      <DefaultHelmet page="sale" />
      <MenuHeader title={SALESTYPE[type as SalesTypeKey]} />
      <div className="w-full pt-[60px] pb-20">
        {salesType === PRODUCT_TYPE_DIRECT ? <DirectGuide /> : <SpeedGuide />}

        {showVerifyGuide ? (
          <>
            <div
              className="fixed top-0 bottom-0 left-0 right-0 z-20 mx-auto max-w-[720px] min-w-[280px]"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
            />
            <div className="fixed bottom-0 left-0 right-0 z-40 mx-auto max-w-[720px] min-w-[280px] rounded-t-2xl bg-white px-5 pt-6 pb-6 shadow-[0_-2px_16px_rgba(0,0,0,0.12)]">
              <div className="flex items-start gap-2">
                <span className="mt-[2px] shrink-0">
                  <InformationCircleIcon color={colors.gray[5]} width={22} height={22} />
                </span>
                <p className="text-[16px] leading-[24px] font-medium text-gray-8">
                  죄송합니다. 본인 인증 후 판매 등록 해주시기 바랍니다.
                </p>
              </div>
              <ul className="mt-4 flex flex-col gap-1.5">
                <li className="flex gap-1.5 text-[14px] leading-[20px] text-gray-8">
                  <span className="shrink-0">•</span>
                  <span>본인 인증 후 판매 차량 등록 가능합니다.</span>
                </li>
                <li className="flex gap-1.5 text-[14px] leading-[20px] text-gray-8">
                  <span className="shrink-0">•</span>
                  <span>로그인 한 사람과 차량의 소유자가 다를 경우 첨부 서류가 필요합니다.</span>
                </li>
              </ul>
              <div className="mt-5">
                <BasicButton
                  name={isVerifying ? '본인 인증 진행 중...' : '본인 인증 하기'}
                  bgColor={colors.primary[10]}
                  borderColor={colors.primary[10]}
                  textColor={colors.gray[0]}
                  fontSize={16}
                  height={52}
                  fontWeight={'bold'}
                  onClick={handleClickVerify}
                  isDisabled={isVerifying}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-[68px] left-0 right-0 h-14 max-w-[720px] min-w-[280px]">
              {salesType === PRODUCT_TYPE_SPEED && (
                <FloatingIconButton
                  bgColor={colors.gray[0]}
                  icon={<PhoneIcon color={colors.primary[10]} />}
                  width={52}
                  height={52}
                  onClick={() => phoneCall(REPRESENTATIVE_NUMBER)}
                />
              )}
            </div>
            <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-14 max-w-[720px] min-w-[280px] bg-white">
              <div className="absolute top-[-16px] left-0 w-full h-[16px] bg-gradient-to-b from-transparent to-[#ffffff]"></div>
              <BasicButton
                name={buttonLabel}
                bgColor={colors.primary[10]}
                borderColor={colors.primary[10]}
                textColor={colors.gray[0]}
                fontSize={16}
                height={48}
                fontWeight={'bold'}
                onClick={handleClickSalesType}
              />
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={isShowErrorPopup}
        content="이미 판매중이거나 판매중지된 차량입니다."
        onLeftButtonClick={() => setIsShowErrorPopup(false)}
        rightButtonLabel="내차관리로"
        onRightButtonClick={onClickGoMyProductPage}
      />

      <AlertModal
        isOpen={isMismatchOpen}
        onClose={() => setIsMismatchOpen(false)}
        title="가입한 휴대전화번호와 인증한 전화번호가 다릅니다"
        content={
          <p className="text-[14px] leading-[22px] text-gray-7 text-center">
            신규 회원가입 또는 가입된 휴대전화 번호로
            <br />
            실명인증 해주세요.
          </p>
        }
        buttonLabel="확인"
      />
    </>
  );
};

export default MyCarSaleGuide;
